import { FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query';
import api from '../../../app/api';
import { PaginatedList } from '../../../shared/models/paginated-list';
import { ClientGroup } from '../models/client-group';
import { ClientGroupDetails } from '../models/client-group-details';
import { ClientGroupDetailsParams } from '../models/client-group-details-params';
import { ClientGroupParams } from '../models/client-group-params';

export const clientGroupsApi = api
  .enhanceEndpoints({
    addTagTypes: ['ClientGroups', 'ClientGroup', 'ClientUsers', 'Templates'],
  })
  .injectEndpoints({
    endpoints: builder => {
      return {
        getClientGroups: builder.query<PaginatedList<ClientGroup>, ClientGroupParams>({
          query: params => ({
            url: '/portal-api/v1/client-groups',
            params,
          }),
          providesTags: ['ClientGroups'],
        }),
        getClientGroup: builder.query<ClientGroupDetails, string>({
          query: id => ({
            url: `/portal-api/v1/client-groups/${id}`,
          }),
          providesTags: result => (result?.id ? [{ type: 'ClientGroup', id: result.id }] : []),
        }),
        createClientGroup: builder.mutation<
          ClientGroupDetailsParams,
          Pick<ClientGroupDetailsParams, 'name' | 'templates' | 'users' | 'customers'>
        >({
          queryFn: async (requestData, _queryApi, _extraOptions, baseQuery) => {
            const clientGroupQuery = (await baseQuery({
              url: '/portal-api/v1/client-groups',
              method: 'POST',
              body: requestData,
            })) as QueryReturnValue<ClientGroupDetailsParams, FetchBaseQueryError, FetchBaseQueryMeta>;

            if (!clientGroupQuery.data) {
              return clientGroupQuery;
            }

            const clientGroupQueryData = clientGroupQuery.data as ClientGroupDetailsParams;

            await Promise.all(
              (requestData?.templates || []).map(
                templateId =>
                  baseQuery({
                    url: `/portal-api/v1/client-groups/${clientGroupQueryData.id}/templates/${templateId}`,
                    method: 'PUT',
                  }) as QueryReturnValue<ClientGroupDetailsParams, FetchBaseQueryError, FetchBaseQueryMeta>,
              ),
            );

            await Promise.all(
              (requestData?.customers || []).map(
                customerId =>
                  baseQuery({
                    url: `/portal-api/v1/client-groups/${clientGroupQueryData.id}/customers/${customerId}`,
                    method: 'PUT',
                  }) as QueryReturnValue<ClientGroupDetailsParams, FetchBaseQueryError, FetchBaseQueryMeta>,
              ),
            );

            await Promise.all(
              (requestData?.users || []).map(
                user =>
                  baseQuery({
                    url: `/portal-api/v1/client-groups/${clientGroupQueryData.id}/users/${user}`,
                    method: 'PUT',
                  }) as QueryReturnValue<ClientGroupDetailsParams, FetchBaseQueryError, FetchBaseQueryMeta>,
              ),
            );
            return clientGroupQuery;
          },
          invalidatesTags: ['ClientGroups', 'ClientUsers', 'Templates'],
        }),
        updateClientGroup: builder.mutation<
          ClientGroupDetails,
          Pick<ClientGroupDetailsParams, 'id' | 'name' | 'templates' | 'users' | 'customers'>
        >({
          async queryFn({ id, name, templates, users, customers }, _queryApi, _extraOptions, baseQuery) {
            const clientGroup = (await baseQuery({
              url: `/portal-api/v1/client-groups/${id}`,
              method: 'PUT',
              body: {
                id,
                name,
              },
            })) as QueryReturnValue<ClientGroupDetails, FetchBaseQueryError, FetchBaseQueryMeta>;

            if (!clientGroup?.data) {
              return clientGroup;
            }

            const clientGroupData = clientGroup.data as ClientGroupDetails;

            const existingTemplatesIds = clientGroupData.templates.map(({ id }) => id);
            const templatesForDeletion = clientGroupData.templates.filter(value => !templates.includes(value.id)) || [];
            const newTemplates = templates.filter(template => !existingTemplatesIds?.includes(template));

            const deleteTemplatesPromises = (templatesForDeletion || []).map(
              ({ id: templateId }) =>
                baseQuery({
                  url: `/portal-api/v1/client-groups/${id}/templates/${templateId}`,
                  method: 'DELETE',
                }) as QueryReturnValue<ClientGroupDetails, FetchBaseQueryError, FetchBaseQueryMeta>,
            );

            const newTemplatesPromises = (newTemplates || []).map(
              templateId =>
                baseQuery({
                  url: `/portal-api/v1/client-groups/${id}/templates/${templateId}`,
                  method: 'PUT',
                }) as QueryReturnValue<ClientGroupDetails, FetchBaseQueryError, FetchBaseQueryMeta>,
            );

            await Promise.all([...deleteTemplatesPromises, ...newTemplatesPromises]);

            const existingUsersIds = clientGroupData.users.map(({ id }) => id);
            const usersForDeletion = clientGroupData.users.filter(user => !users.includes(user.id)) || [];
            const newUsers = users.filter(user => !existingUsersIds?.includes(user));

            const deleteUsersPromises = (usersForDeletion || []).map(
              ({ id: user }) =>
                baseQuery({
                  url: `/portal-api/v1/client-groups/${id}/users/${user}`,
                  method: 'DELETE',
                }) as QueryReturnValue<ClientGroupDetails, FetchBaseQueryError, FetchBaseQueryMeta>,
            );

            const newUsersPromises = (newUsers || []).map(
              user =>
                baseQuery({
                  url: `/portal-api/v1/client-groups/${id}/users/${user}`,
                  method: 'PUT',
                }) as QueryReturnValue<ClientGroupDetails, FetchBaseQueryError, FetchBaseQueryMeta>,
            );

            await Promise.all([...deleteUsersPromises, ...newUsersPromises]);

            const existingCustomersIds = clientGroupData.customers.map(({ id }) => id);
            const customersForDeletion = clientGroupData.customers.filter(value => !customers.includes(value.id)) || [];
            const newCustomers = customers.filter(customer => !existingCustomersIds?.includes(customer));

            const deleteCustomersPromises = (customersForDeletion || []).map(
              ({ id: customerId }) =>
                baseQuery({
                  url: `/portal-api/v1/client-groups/${id}/customers/${customerId}`,
                  method: 'DELETE',
                }) as QueryReturnValue<ClientGroupDetails, FetchBaseQueryError, FetchBaseQueryMeta>,
            );

            const newCustomersPromises = (newCustomers || []).map(
              customerId =>
                baseQuery({
                  url: `/portal-api/v1/client-groups/${id}/customers/${customerId}`,
                  method: 'PUT',
                }) as QueryReturnValue<ClientGroupDetails, FetchBaseQueryError, FetchBaseQueryMeta>,
            );

            await Promise.all([...deleteCustomersPromises, ...newCustomersPromises]);
            return clientGroup;
          },
          invalidatesTags: result =>
            result?.id ? ['ClientGroups', 'ClientUsers', 'Templates', { type: 'ClientGroup', id: result.id }] : [],
        }),
        deleteClientGroup: builder.mutation<ClientGroupDetails, ClientGroupDetailsParams['id']>({
          async queryFn(clientGroupId, _queryApi, _extraOptions, baseQuery) {
            const clientGroup = (await baseQuery({
              url: `/portal-api/v1/client-groups/${clientGroupId}`,
              method: 'GET',
            })) as QueryReturnValue<ClientGroupDetails, FetchBaseQueryError, FetchBaseQueryMeta>;

            if (!clientGroup?.data) {
              return clientGroup;
            }
            const clientGroupData = clientGroup.data as ClientGroupDetails;

            const templatesIds = clientGroupData.templates.map(({ id }) => id);

            const deleteTemplatesPromises = (clientGroupData.templates || []).map(
              () =>
                baseQuery({
                  url: `/portal-api/v1/client-groups/${clientGroupData.id}/templates/${templatesIds}`,
                  method: 'DELETE',
                }) as QueryReturnValue<ClientGroupDetailsParams, FetchBaseQueryError>,
            );

            await Promise.all([...deleteTemplatesPromises]);

            await baseQuery({
              url: `/portal-api/v1/client-groups/${clientGroupId}`,
              method: 'DELETE',
            });

            return clientGroup;
          },
          invalidatesTags: result =>
            result?.id ? ['ClientGroups', 'ClientUsers', 'Templates', { type: 'ClientGroup', id: result.id }] : [],
        }),
      };
    },
    overrideExisting: false,
  });

export const {
  useGetClientGroupsQuery,
  useGetClientGroupQuery,
  useCreateClientGroupMutation,
  useUpdateClientGroupMutation,
  useDeleteClientGroupMutation,
} = clientGroupsApi;
